import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, XCircle, Loader2, MessageCircle, Home } from "lucide-react";
import Nav from "../components/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/5511989666465?text=Ol%C3%A1%2C+acabei+de+contratar+um+plano+na+Triad.";

function useQuery() {
  return new URLSearchParams(window.location.search);
}

export default function PaymentReturn() {
  const q = useQuery();
  const order_nsu = q.get("order_nsu");
  const transaction_nsu = q.get("transaction_nsu");
  const slug = q.get("slug");
  const capture_method = q.get("capture_method");
  const receipt_url = q.get("receipt_url");

  const [status, setStatus] = useState("loading"); // loading | approved | pending | rejected
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem("triad_last_order") || "null");
  } catch (_) {}

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!order_nsu) {
        setStatus("rejected");
        return;
      }
      try {
        const res = await fetch(`${API}/checkout/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_nsu, transaction_nsu, slug }),
        });
        const data = await res.json();
        if (cancelled) return;
        setOrder(data.order || null);
        if (data.status === "approved") {
          setStatus("approved");
        } else if (attempts < 6) {
          // Poll a few times in case webhook is still arriving
          setStatus("pending");
          setTimeout(() => setAttempts((a) => a + 1), 2500);
        } else {
          setStatus("pending");
        }
      } catch (e) {
        if (!cancelled) setStatus("rejected");
      }
    }
    verify();
    return () => { cancelled = true; };
  }, [order_nsu, transaction_nsu, slug, attempts]);

  return (
    <main
      data-testid="payment-return-page"
      className="min-h-screen bg-ink-950 text-white/90"
      style={{
        background:
          "radial-gradient(ellipse 90% 60% at 50% 0%, rgba(10,77,255,0.25), transparent 60%), linear-gradient(180deg, #050505 0%, #030817 60%, #061436 100%)",
      }}
    >
      <Nav />
      <div className="mx-auto max-w-2xl px-6 pt-40 pb-24 flex flex-col items-center text-center">
        {status === "loading" && (
          <div className="flex flex-col items-center gap-6 py-20" data-testid="payment-loading">
            <Loader2 className="w-12 h-12 text-blue-soft animate-spin" />
            <p className="text-white/60">Confirmando seu pagamento…</p>
          </div>
        )}

        {status === "approved" && (
          <Approved order={order} stored={stored} capture_method={capture_method} receipt_url={receipt_url} />
        )}
        {status === "pending" && (
          <Pending capture_method={capture_method} receipt_url={receipt_url} />
        )}
        {status === "rejected" && <Rejected planId={stored?.plan_id} />}
      </div>
    </main>
  );
}

function StatusIcon({ children, color, glow }) {
  return (
    <motion.div
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="w-24 h-24 rounded-full flex items-center justify-center mb-8"
      style={{ background: `${color}1A`, boxShadow: `0 0 60px -8px ${glow}` }}
    >
      {children}
    </motion.div>
  );
}

function Approved({ order, stored, capture_method, receipt_url }) {
  const brl = (c) => (c != null ? `R$ ${(c / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—");
  const method = capture_method === "pix" ? "PIX" : capture_method === "credit_card" ? "Cartão de crédito" : "—";
  const today = new Date().toLocaleDateString("pt-BR");
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="payment-approved">
      <StatusIcon color="#22C55E" glow="rgba(34,197,94,0.6)">
        <CheckCircle2 className="w-12 h-12 text-green-400" strokeWidth={1.5} />
      </StatusIcon>
      <h1 className="font-serif text-4xl md:text-5xl font-bold text-white tracking-tight">
        Pagamento confirmado!
      </h1>
      <p className="mt-3 text-blue-soft text-lg">Seja bem-vindo à Triad Marketing.</p>
      <p className="mt-5 text-white/60 leading-relaxed max-w-lg mx-auto">
        Nossa equipe entrará em contato pelo WhatsApp para iniciar o atendimento, solicitar os acessos necessários e organizar o planejamento da sua empresa.
      </p>

      {/* Receipt card */}
      <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 md:p-8 text-left space-y-3">
        <Row k="Plano" v={order?.plan_name || stored?.plan_name || "—"} />
        <Row k="Valor pago" v={brl(order?.paid_amount ?? order?.amount)} />
        <Row k="Forma de pagamento" v={method} />
        <Row k="Data da contratação" v={today} />
        <Row k="Número do pedido" v={order?.order_nsu || stored?.order_nsu || "—"} mono />
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="approved-whatsapp"
          className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.2em] font-bold text-white bg-[#22C55E] hover:bg-[#16A34A] shadow-[0_10px_30px_-8px_rgba(34,197,94,0.55)] transition-all duration-300 hover:scale-[1.03]"
        >
          <MessageCircle className="w-4 h-4" /> Falar com a equipe
        </a>
        {receipt_url && (
          <a href={receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-white border border-white/15 hover:bg-white/5 transition-colors">
            Ver comprovante
          </a>
        )}
        <a href="/" data-testid="approved-home" className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-white border border-white/15 hover:bg-white/5 transition-colors">
          <Home className="w-4 h-4" /> Início
        </a>
      </div>
    </motion.div>
  );
}

function Pending({ capture_method, receipt_url }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="payment-pending">
      <StatusIcon color="#6B8AFF" glow="rgba(107,138,255,0.5)">
        <Clock className="w-12 h-12 text-blue-soft" strokeWidth={1.5} />
      </StatusIcon>
      <h1 className="font-serif text-4xl md:text-5xl font-bold text-white tracking-tight">
        Seu pagamento está sendo processado.
      </h1>
      <p className="mt-5 text-white/60 leading-relaxed max-w-lg mx-auto">
        Assim que a InfinitePay confirmar a aprovação, seu plano será ativado automaticamente. Você pode acompanhar por aqui — atualizamos o status em tempo real.
      </p>
      <div className="mt-8 flex items-center justify-center gap-2 text-white/50 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Verificando aprovação…
      </div>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        {receipt_url && (
          <a href={receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-white border border-white/15 hover:bg-white/5 transition-colors">
            Ver comprovante
          </a>
        )}
        <a href="/" className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-white border border-white/15 hover:bg-white/5 transition-colors">
          <Home className="w-4 h-4" /> Voltar ao início
        </a>
      </div>
    </motion.div>
  );
}

function Rejected() {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="payment-rejected">
      <StatusIcon color="#EF4444" glow="rgba(239,68,68,0.5)">
        <XCircle className="w-12 h-12 text-red-400" strokeWidth={1.5} />
      </StatusIcon>
      <h1 className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight">
        Não foi possível concluir o pagamento.
      </h1>
      <p className="mt-5 text-white/60 leading-relaxed max-w-lg mx-auto">
        Verifique os dados informados ou escolha outra forma de pagamento. Nenhum valor foi cobrado.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <a href="/#servicos" data-testid="rejected-retry" className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.2em] font-bold text-white bg-[#22C55E] hover:bg-[#16A34A] transition-all duration-300 hover:scale-[1.03]">
          Tentar novamente
        </a>
        <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-white border border-white/15 hover:bg-white/5 transition-colors">
          <MessageCircle className="w-4 h-4" /> Falar com o suporte
        </a>
      </div>
    </motion.div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
      <span className="text-white/45 text-xs uppercase tracking-[0.18em]">{k}</span>
      <span className={`text-white text-sm font-medium ${mono ? "font-mono text-blue-soft" : ""}`}>{v}</span>
    </div>
  );
}
